import { Service, inject } from '@angular/core';
import { CheckInStatusEnum } from 'app/shared/enums/check-in-status-type.enum';
import { RolesEnum } from 'app/shared/enums/roles.enum';
import { AgencyStoreDto, AgencyStoreVm } from 'app/shared/models/agency-store.model';
import { AgencyDto } from 'app/shared/models/agency.model';
import { CustomerCategoryDTO } from 'app/shared/models/customer-category.model';
import { HttpBasicRequest } from 'app/shared/models/http-basic-request.model';
import { PagedResponse } from 'app/shared/models/paged-response.model';
import { UserStatusEnumDTO } from 'app/shared/models/user-info.model';
import { BasicHttpClientService } from 'app/shared/services/basic-http-client.service';
import { environment } from 'environments/environment';
import { Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { LocationFullDTO } from '../../../../shared/models/location.model';
import { ContactList, ContactVM } from '../../models/back-office-admin-common/contact.models';
import { CustomerCheckinVM, ICompanyForm } from '../../models/back-office-admin-common/customer-check-in.model';
import { TransferPosResponseModel } from '../../models/point-of-sale/transfer-pos.model';
import { AgencyListItemVM, AgencyVM } from '../../models/salesNetwork/agency.model';
import { AgentDTO } from '../../models/users/agent.models';
import { AgencyMapper } from '../../utilities/agency-mapper';
import { ContactCRUD } from '../../utilities/contact-crud';
import { LocationMapper } from '../../utilities/location-mapper';
import { SessionStore } from 'app/shared/state/session.store';
import { ShopContextStore } from 'app/shared/state/shop-context.store';
import { HttpParams } from '@angular/common/http';

export interface GetAllAgenciesParams {
  skipCount?: number;
  maxResultCount?: number;
  searchString?: string;
  includeLocation?: boolean;
  checkInStatus?: CheckInStatusEnum;
  storeId?: number;
  sorting?: string;
}

@Service()
export class AgencyService {
  private _httpClient = inject(BasicHttpClientService);

  private baseUrl: string;

  private _reloadAG = new Subject<boolean>();
  public get reloadAgency(): Observable<boolean> { return this._reloadAG.asObservable(); }

  private _session = inject(SessionStore);
  private _shopContext = inject(ShopContextStore);

  constructor() {
    this.baseUrl = environment.urls.apiUrl.url;
  }

getAllAgencies(
  loaderId: string,
  params: GetAllAgenciesParams = {}
): Observable<PagedResponse<AgencyListItemVM>> {

  const {
    skipCount,
    maxResultCount,
    searchString = '',
    includeLocation = false,
    checkInStatus,
    storeId,
    sorting = 'name'
  } = params;

    let queryParams = new HttpParams()
      .set('IncludeLocation', includeLocation)
      .set('searchString', searchString)
      .set('sorting', sorting);

    if (skipCount !== undefined) {
      queryParams = queryParams.set('skipCount', skipCount);
    }

    if (maxResultCount !== undefined) {
      queryParams = queryParams.set('maxResultCount', maxResultCount);
    }

    if (checkInStatus !== undefined && checkInStatus !== null) {
      queryParams = queryParams.set('checkInStatus', checkInStatus);
    }

    if (storeId !== undefined && storeId !== null) {
      queryParams = queryParams.set('storeId', storeId);
    }

    return this._httpClient.get<PagedResponse<AgencyDto>>(
      this.baseUrl + '/agencies',
      { params: queryParams },
      loaderId
    ).pipe(
      map(data => new PagedResponse<AgencyListItemVM>({
        items: data.items.map(x =>
          AgencyMapper.AgencyListItemVM(
            x,
            null,
            x.storeActivations.find(x => x.storeId === storeId)
          )
        ),
        totalCount: data.totalCount
      }))
    );
  }

  getStorePosAgencies(posId: number, loaderId: string): Observable<AgencyListItemVM[]> {

    return this._httpClient.get<AgencyDto[]>(
      this.baseUrl + '/point-of-sales/' + posId + '/agencies/' + this._shopContext.currentShop().id, null, loaderId).pipe(map(data =>
        data.filter(x =>
          (!this._session.userInfo().agency || (x.id === this._session.userInfo().agency.id))
        ).map(x => AgencyMapper.AgencyListItemVM(x, null, x.storeActivations.find(x => x.storeId === this._shopContext.currentShop().id)))));
  }

  getAgency(id: number, loaderId?: string): Observable<AgencyDto> {
    return this._httpClient.get<AgencyDto>(this.baseUrl + `/agencies/${id}`, {}, loaderId);
  }

  getMultipleAgencyById(agenciesId: number[], loaderId: string): Observable<any[]> {
    const agenciesRequest: HttpBasicRequest[] = [];
    agenciesId.forEach(id => {
      if (id) {
        agenciesRequest.push({ url: this.baseUrl + `/agencies/${id}`, method: 'get' });
      }
    });
    return this._httpClient.forkJoin<AgencyDto>(agenciesRequest, loaderId);
  }

  getAgencyDetail(loaderId: string, agency: AgencyDto): Observable<AgencyVM> {
    const result = new Subject<AgencyVM>();
    const params = { skipCount: 0, maxResultCount: 0, sorting: 'name' };
    this._httpClient.forkJoin<any>([
      { url: this.baseUrl + `/locations/${agency.locationId}`, method: 'get' },
      { url: this.baseUrl + `/agencies/${agency.id}/contacts`, method: 'get' },
      { url: this.baseUrl + `/company-forms`, method: 'get', options: { params } },
      { url: this.baseUrl + `/customer-categories`, method: 'get' },
    ], loaderId).subscribe(res => {
      const location: LocationFullDTO = res[0];
      const contacts: ContactVM[] = res[1];
      const agencyForms: PagedResponse<ICompanyForm> = res[2];
      const agencyCategories: PagedResponse<CustomerCategoryDTO> = res[3];
      result.next(AgencyMapper.AgencyDetail(agency, location, contacts, agencyForms.items, agencyCategories.items));
    });

    return result.asObservable();

  }

  saveAgency(agencyVM: AgencyVM, cacheAgencyVm: AgencyVM, checkInStatus: CheckInStatusEnum, loaderId: string, agencyStore: AgencyStoreVm[], agencyStoreCache: AgencyStoreVm[]) {
    const result = new Subject();
    agencyVM.checkInStatus = checkInStatus === CheckInStatusEnum.OnHold ? environment.tenants?.find(t => t.id?.toLowerCase() === this._session.userInfo()?.tenant?.id?.toLowerCase())?.putAgencyInOnHold ? checkInStatus : CheckInStatusEnum.Approved : checkInStatus;
    let requests: HttpBasicRequest[] = [
      { url: this.baseUrl + `/agencies/${agencyVM.id}`, method: 'put', body: AgencyMapper.agencyDTO(agencyVM) },
      { url: this.baseUrl + `/locations/${agencyVM.location.id}`, method: 'put', body: agencyVM.location }
    ];

    if (checkInStatus === CheckInStatusEnum.OnHold && agencyVM.id)
      requests.push({
        method: 'get',
        url: this.baseUrl + '/agencies/agents',
        options: { params: { agencyId: agencyVM.id, skipCount: 0, maxResultCount: 0 } }
      });

    requests = requests.concat(AgencyMapper.UpdateOrAssociateAgencyStore(agencyStore, agencyStoreCache, this.baseUrl));

    this._httpClient.forkJoin(requests, loaderId).subscribe(res => {
      ContactCRUD.ContactCreateOrEditOrDelete(loaderId, this._httpClient, this.baseUrl, agencyVM.contactList, cacheAgencyVm.contactList, 'agency', agencyVM.id).pipe(filter(res => !!res)).subscribe(() => {
      // ContactCRUD.ContactCreateOrEditOrDeleteOld(loaderId, this._httpClient, this.baseUrl, agencyVM.contactList, cacheAgencyVm.contactList, 'agency', agencyVM.id).pipe(filter(res => !!res)).subscribe(() => {
        if (checkInStatus === CheckInStatusEnum.OnHold && agencyVM.id && res && res[requests.length - 1]) {
          const usersData: PagedResponse<AgentDTO> = res[requests.length - 1] as PagedResponse<AgentDTO>;
          requests = usersData?.items?.filter(x => x.user.status === UserStatusEnumDTO.AwaitingAproval).map(a => new HttpBasicRequest({
            url: this.baseUrl + '/users/activate?userIdToActivate=' + a.user.id,
            // body: {
            // 	userIdToActivate: a.user.id
            // },
            method: 'post'
          }));
          if (requests?.length)
            this._httpClient.forkJoin(requests, loaderId).subscribe(() => {
              result.next(res ? res[0] : null);
              this._reloadAG.next(true);
            });
          else {
            this._reloadAG.next(true);
            result.next(res ? res[0] : null);
          }
        } else {
          this._reloadAG.next(true);
          result.next(res ? res[0] : null);
        }
      });
    }, error => {
      console.error(error);
    });
    return result;
  }

  registerAgency(agencyVM: CustomerCheckinVM, loaderId: string) {
    const result = new Subject<AgencyVM>();
    var agencyRequest = AgencyMapper.RegisterAgencyMap(agencyVM);
    this._httpClient.post(this.baseUrl + '/agencies/register', agencyRequest, { headers: { 'Content-Type': 'application/json' } }, loaderId).subscribe(res => {
      result.next(undefined);
    });
    return result.asObservable();
  }

  createAgency(agencyVM: CustomerCheckinVM, loaderId: string): Observable<AgencyDto> {
    const result = new Subject<AgencyVM>();
    const location: HttpBasicRequest[] = LocationMapper.locationCreateRequest(agencyVM.companyInfo.location, this.baseUrl);
    this._httpClient.forkJoin(location, loaderId).subscribe((locResp: any) => {
      var locationId = locResp[0]?.id;

      var agencyInfo = new AgencyDto();

      agencyVM.companyInfo.checkInStatus = this._session.userInfo()?.mainRole <= RolesEnum.StoreUser ? (environment.tenants?.find(t => t.id?.toLowerCase() === this._session.userInfo()?.tenant?.id?.toLowerCase())?.putAgencyInOnHold ? CheckInStatusEnum.OnHold : CheckInStatusEnum.Approved) : CheckInStatusEnum.ToApprove;
      agencyInfo.checkInStatus = agencyVM.companyInfo.checkInStatus;
      agencyInfo.name = agencyVM.companyInfo.name;
      agencyInfo.fiscalCode = agencyVM.companyInfo.fiscalCode;
      agencyInfo.vatNumber = agencyVM.companyInfo.vatNumber;
      agencyInfo.locationId = locationId;

      const agency: HttpBasicRequest[] = AgencyMapper.agencyInfoCreateRequest(agencyInfo, this.baseUrl);
      this._httpClient.forkJoin(agency, loaderId).subscribe((agencyResp: any) => {
        var agency = agencyResp[0];
        var agencyId = agencyResp[0]?.id;
        ContactCRUD.ContactCreateOrEditOrDelete(loaderId, this._httpClient, this.baseUrl, agencyVM.contactInfo, [], 'agency', agencyId).pipe(filter(res => !!res)).subscribe(() => {
          // ContactCRUD.ContactCreateOrEditOrDeleteOld(loaderId, this._httpClient, this.baseUrl, agencyVM.contactInfo, new ContactList(), 'agency', agencyId).pipe(filter(res => !!res)).subscribe(() => {
          result.next(agency);
        });
      });
    });
    return result.asObservable();
  }

  deleteAgency(agencyId: number, loaderId: string) {
    return this._httpClient.delete(`${this.baseUrl}/agencies/${agencyId}`, {}, loaderId);
  }

  transferPos(sourceAgencyStoreId: number, destinationAgencyStoreId: number, loaderId: string): Observable<TransferPosResponseModel> {
    return this._httpClient.post(`${this.baseUrl}/agencies/transfer-points-of-sale`, {
      sourceAgencyStoreId,
      destinationAgencyStoreId
    }, null, loaderId);
  }

}
